import { useCallback, useEffect, useReducer } from "react";

import { ModeType } from "@/lib/modes";
import { VoiceChatMessage } from "@/lib/messaging";

/**
 * State management for VoiceChat page using reducer pattern.
 *
 * Replaces 10+ useState calls with unified state management for:
 * - Mode selection and context
 * - Message routing (main conversation vs translation)
 * - Translation panel state
 * - Processing status
 *
 * Benefits:
 * - Atomic state updates (no race conditions)
 * - Predictable state transitions
 * - Easier to test and debug
 */

export interface VoiceChatState {
  /** Current active mode */
  mode: ModeType;

  /** Setup context/instructions for current mode */
  modeContext: string;

  /** Mode that was active when user started speaking */
  modeSpeakingStarted: ModeType;

  /** Current message from server for main conversation */
  currentMessage: VoiceChatMessage | undefined;

  /** Translation panel state */
  translationPanel: {
    isOpen: boolean;
    message: VoiceChatMessage | undefined;
  };
}

export type VoiceChatAction =
  | { type: "MODE_SELECTED"; mode: ModeType }
  | { type: "MODE_SETUP_RECEIVED"; context: string }
  | { type: "MODE_SPEAKING_STARTED"; mode: ModeType }
  | { type: "MESSAGE_RECEIVED"; message: VoiceChatMessage }
  | { type: "TRANSLATION_MESSAGE_RECEIVED"; message: VoiceChatMessage }
  | { type: "TRANSLATION_PANEL_TOGGLE"; isOpen: boolean }
  | { type: "CLEAR_MESSAGES" }
  | { type: "RESET" };

function voiceChatReducer(
  state: VoiceChatState,
  action: VoiceChatAction,
): VoiceChatState {
  switch (action.type) {
    case "MODE_SELECTED":
      // When mode changes, clear everything atomically
      return {
        ...state,
        mode: action.mode,
        modeContext: "",
        currentMessage: undefined,
        modeSpeakingStarted: action.mode,
      };

    case "MODE_SETUP_RECEIVED":
      return {
        ...state,
        modeContext: action.context,
      };

    case "MODE_SPEAKING_STARTED":
      return {
        ...state,
        modeSpeakingStarted: action.mode,
      };

    case "MESSAGE_RECEIVED":
      return {
        ...state,
        currentMessage: action.message,
      };

    case "TRANSLATION_MESSAGE_RECEIVED":
      return {
        ...state,
        translationPanel: {
          ...state.translationPanel,
          message: action.message,
        },
      };

    case "TRANSLATION_PANEL_TOGGLE":
      return {
        ...state,
        translationPanel: {
          ...state.translationPanel,
          isOpen: action.isOpen,
        },
      };

    case "CLEAR_MESSAGES":
      return {
        ...state,
        currentMessage: undefined,
        modeContext: "",
      };

    case "RESET":
      return {
        ...state,
        modeContext: "",
        currentMessage: undefined,
        translationPanel: {
          isOpen: false,
          message: undefined,
        },
      };

    default:
      return state;
  }
}

/**
 * Module-level snapshot for bug reporting.
 * Updated by the active useVoiceChatReducer instance so that useBugReport
 * can read the real voicechat state without creating its own reducer instance.
 */
let _reportingSnapshot: {
  state: VoiceChatState | null;
  lastAction: VoiceChatAction | null;
} = { state: null, lastAction: null };

export function getVoiceChatReportingSnapshot() {
  return _reportingSnapshot;
}

/**
 * Hook to manage VoiceChat page state with reducer pattern.
 *
 * @param initialMode - Initial mode to start with
 *
 * @example
 * ```tsx
 * const { state, dispatch } = useVoiceChatReducer(Modes.LangCoach);
 *
 * // Change mode (atomically clears messages)
 * dispatch({ type: 'MODE_SELECTED', mode: Modes.DailyLife });
 *
 * // Receive message from server
 * dispatch({ type: 'MESSAGE_RECEIVED', message: serverMessage });
 *
 * // Toggle translation panel
 * dispatch({ type: 'TRANSLATION_PANEL_TOGGLE', isOpen: true });
 * ```
 */
export function useVoiceChatReducer(initialMode: ModeType) {
  const initialState: VoiceChatState = {
    mode: initialMode,
    modeContext: "",
    modeSpeakingStarted: initialMode,
    currentMessage: undefined,
    translationPanel: {
      isOpen: false,
      message: undefined,
    },
  };

  const [state, dispatch] = useReducer(voiceChatReducer, initialState);

  // Keep the module-level snapshot in sync for bug reporting
  useEffect(() => {
    _reportingSnapshot = { ..._reportingSnapshot, state };
  }, [state]);

  // Helper functions for common actions
  const selectMode = useCallback((mode: ModeType) => {
    const action: VoiceChatAction = { type: "MODE_SELECTED", mode };
    _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
    dispatch(action);
  }, []);

  const setModeContext = useCallback((context: string) => {
    const action: VoiceChatAction = { type: "MODE_SETUP_RECEIVED", context };
    _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
    dispatch(action);
  }, []);

  const setModeSpeakingStarted = useCallback((mode: ModeType) => {
    const action: VoiceChatAction = { type: "MODE_SPEAKING_STARTED", mode };
    _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
    dispatch(action);
  }, []);

  const setCurrentMessage = useCallback(
    (message: VoiceChatMessage | undefined) => {
      if (message) {
        const action: VoiceChatAction = { type: "MESSAGE_RECEIVED", message };
        _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
        dispatch(action);
      } else {
        const action: VoiceChatAction = { type: "CLEAR_MESSAGES" };
        _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
        dispatch(action);
      }
    },
    [],
  );

  const setTranslationMessage = useCallback(
    (message: VoiceChatMessage | undefined) => {
      if (message) {
        const action: VoiceChatAction = {
          type: "TRANSLATION_MESSAGE_RECEIVED",
          message,
        };
        _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
        dispatch(action);
      }
    },
    [],
  );

  const toggleTranslationPanel = useCallback((isOpen: boolean) => {
    const action: VoiceChatAction = {
      type: "TRANSLATION_PANEL_TOGGLE",
      isOpen,
    };
    _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
    dispatch(action);
  }, []);

  const reset = useCallback(() => {
    const action: VoiceChatAction = { type: "RESET" };
    _reportingSnapshot = { state: _reportingSnapshot.state, lastAction: action };
    dispatch(action);
  }, []);

  return {
    state,
    dispatch,
    // Helper functions for convenience
    selectMode,
    setModeContext,
    setModeSpeakingStarted,
    setCurrentMessage,
    setTranslationMessage,
    toggleTranslationPanel,
    reset,
  };
}
